/**
 * Users API Type Definitions
 * Defines interfaces and types for user management functionality
 */

/**
 * User type for fleet member roles
 */
export type FleetUserType = 'SubMaster' | 'User';

/**
 * Login application type
 */
export type LoginApp = 'fleeta' | 'blackvue';

/**
 * Token type for requests
 */
export type TokenType = 'web' | 'app';

/**
 * Invite member request parameters interface
 */
export interface InviteMemberParams {
  email: string;
  user_token: string;
  guestEmail: string;
  userType: FleetUserType;
  tokenType?: TokenType;
  loginApp?: LoginApp;
}

/**
 * Invite member API response interface
 */
export interface InviteMemberResponse {
  resultcode: string;
  message: string;
}

/**
 * Invite member error codes mapping
 */
export const INVITE_MEMBER_ERROR_MESSAGES = {
  BC_ERR_INVALID_DATA: 'Invalid data provided',
  BC_ERR_DUPLICATED: 'Email is already registered or invitation already exists', 
  BC_ERR_AUTHENTICATION: 'Authentication failed - please check your credentials',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter provided',
  BC_ERR_UNAVAILABLE: 'Already have subscription - cannot invite more members',
  BC_ERR_INTERNAL_ERROR: 'Internal server error occurred'
} as const;

/**
 * Fleet user type display names
 */
export const FLEET_USER_TYPE_NAMES: Record<FleetUserType, string> = {
  'SubMaster': 'Fleet Manager',
  'User': 'Driver'
};

/**
 * Login app display names
 */
export const LOGIN_APP_NAMES: Record<LoginApp, string> = {
  'fleeta': 'FleetA',
  'blackvue': 'BlackVue'
};

/**
 * Group information for invited member
 */
export interface InvitedMemberGroup {
  groupManageMentID: string;
  groupName: string;
}

/**
 * Invited member information interface
 */
export interface InvitedMemberInfo {
  email: string;
  role: 'Master' | 'SubMaster' | 'User';
  groups: InvitedMemberGroup[];
  dateInvited: string;
  mailStatus: string;
  firstName: string;
  lastName: string;
  profileImg: string;
  hostEmail: string;
  hostfirstName: string;
  hostlastName: string;
  hostRole: 'Master' | 'SubMaster' | 'User';
}

/**
 * Get invite member list request parameters interface
 */
export interface GetInviteMemberListParams {
  email: string;
  user_token: string;
  tokenType?: TokenType;
  startIndex?: number;
  endIndex?: number;
  ordering?: 0 | 1; // 0: newest(default), 1: oldest
  searchKey: string; // Required - use empty string if no search
}

/**
 * Get invite member list API response interface
 */
export interface GetInviteMemberListResponse {
  resultcode: string;
  message: string;
  response?: {
    inviteMember: InvitedMemberInfo[];
    inviteMemberCount: number;
  };
}

/**
 * Get invite member list error codes mapping
 */
export const GET_INVITE_MEMBER_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed - please check your credentials',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter provided',
  BC_ERR_UNAVAILABLE: 'Already joined BlackVueCloud',
  BC_ERR_SERVER: 'Internal server error occurred',
  BC_ERR_PERMISSION: 'Permission error - insufficient privileges'
} as const;

/**
 * Delete member request parameters interface
 */
export interface DeleteMemberParams {
  email: string;
  user_token: string;
  deleteEmail: string;
  deleteEmailUserType: FleetUserType;
  tokenType?: TokenType;
  loginApp?: LoginApp;
}

/**
 * Delete member API response interface
 */
export interface DeleteMemberResponse {
  resultcode: string;
  message: string;
  response?: {};
}

/**
 * Delete member error codes mapping
 */
export const DELETE_MEMBER_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed - please check your credentials',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter provided',
  BC_ERR_SERVER: 'Internal server error occurred',
  BC_ERR_PERMISSION: 'Permission error - insufficient privileges',
  BC_ERR_ALREADY_REQUESTED: 'Member has already been deleted'
} as const;

/**
 * Mail status display names
 */
export const MAIL_STATUS_NAMES = {
  'A': 'Active',
  'P': 'Pending',
  'C': 'Completed',
  'F': 'Failed'
} as const;

/**
 * User role display names (extended for invited members)
 */
export const USER_ROLE_NAMES = {
  'Master': 'Fleet Master',
  'SubMaster': 'Fleet Manager',
  'User': 'Driver'
} as const;

/**
 * User permissions interface
 * Defines all available permissions for fleet management
 */
export interface UserPermissions {
  // Device Management Permissions
  listDashcam: boolean;
  registerDashcam: boolean;
  changeDashcamName: boolean;
  deleteDashcam: boolean;
  dashcamInformation: boolean;
  
  // Device Settings Permissions
  firmwareSettings: boolean;
  privacy: boolean;
  eventAutoUpload: boolean;
  firmwareUpdate: boolean;
  pushNotifications: boolean;
  cloudUploadManual: boolean;
  
  // Streaming and Media Permissions
  vodDownload: number; // 0: unlimited, -1: disabled, >0: seconds limit
  myBlackVueFilePlay: boolean;
  cloudFilePlay: boolean;
  fileDelete: boolean;
  liveView: number; // 0: unlimited, -1: disabled, >0: seconds limit
  fileMove: boolean;
  
  // Tracking and Monitoring Permissions  
  gpsTracking: boolean;
  geoFence: boolean;
  driveReports: boolean;
  
  // User Management Permissions
  membersInquiry: boolean;
  inviteSubMaster: boolean;
  inviteUser: boolean;
  deleteSubMaster: boolean;
  deleteUser: boolean;
  
  // Group Management Permissions
  registerGroup: boolean;
  deleteGroup: boolean;
  changeGroupName: boolean;
  assignDashcamToGroup: boolean;
  deleteDashcamFromGroup: boolean;
  assignSubMasterToGroup: boolean;
  deleteSubMasterFromGroup: boolean;
  assignUserToDashcam: boolean;
  deleteUserFromDashcam: boolean;
  
  // Advanced Permissions
  setSubMasterPermissions: boolean;
  inquirySubMasterPermissions: boolean;
  setPushNotification: boolean;
  setEmailNotification: boolean;
  sharingVideoToEventMap: boolean;
  
  // Storage Information
  cloudStorageUsage: number; // Storage usage in bytes
  
  // Compliance Features
  eldHosCompliance?: boolean; // ELD HOS Compliance (optional)
}

/**
 * Get user permissions request parameters interface
 */
export interface GetUserPermissionsParams {
  email: string;
  user_token: string;
  tokenType?: TokenType;
}

/**
 * Get user permissions API response interface
 */
export interface GetUserPermissionsResponse {
  resultcode: string;
  message: string;
  response?: {
    permissions: UserPermissions;
  };
}

/**
 * Get user permissions error codes mapping
 */
export const GET_USER_PERMISSIONS_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed - please check your credentials',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter provided',
  BC_ERR_SERVER: 'Internal server error occurred',
  BC_ERR_UNAVAILABLE: 'UserType is not valid or subscription information not found'
} as const;

/**
 * Get target user permissions request parameters interface (Master queries SubMaster/User)
 */
export interface GetTargetUserPermissionsParams {
  email: string;
  user_token: string;
  targetEmail: string;
  tokenType?: TokenType;
}

/**
 * Get target user permissions API response interface (Master queries SubMaster/User)
 */
export interface GetTargetUserPermissionsResponse {
  resultcode: string;
  message: string;
  response?: {
    permissions: UserPermissions;
  };
}

/**
 * Get target user permissions error codes mapping
 */
export const GET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed - please check your credentials',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter provided',
  BC_ERR_SERVER: 'Internal server error occurred',
  BC_ERR_UNAVAILABLE: 'UserType is not valid, unsupported target email, or subscription information not found'
} as const;

/**
 * Set target user permissions request parameters interface (Master sets SubMaster/User permissions)
 */
export interface SetTargetUserPermissionsParams {
  email: string;
  user_token: string;
  roleSetEmail: string;
  permissions: UserPermissions;
  tokenType?: TokenType;
}

/**
 * Set target user permissions API response interface (Master sets SubMaster/User permissions)
 */
export interface SetTargetUserPermissionsResponse {
  resultcode: string;
  message: string;
  response?: {};
}

/**
 * Set target user permissions error codes mapping
 */
export const SET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES = {
  BC_ERR_AUTHENTICATION: 'Authentication failed - please check your credentials',
  BC_ERR_INVALID_PARAMETER: 'Invalid parameter provided',
  BC_ERR_SERVER: 'Internal server error occurred',
  BC_ERR_UNAVAILABLE: 'UserType is not valid or unsupported target email'
} as const;

/**
 * Permission display names for UI
 */
export const PERMISSION_DISPLAY_NAMES: Record<keyof UserPermissions, string> = {
  // Device Management
  listDashcam: 'List Dashcams',
  registerDashcam: 'Register Dashcam',
  changeDashcamName: 'Change Dashcam Name',
  deleteDashcam: 'Delete Dashcam',
  dashcamInformation: 'Dashcam Information',
  
  // Device Settings
  firmwareSettings: 'Firmware Settings',
  privacy: 'Privacy Settings',
  eventAutoUpload: 'Event Auto Upload',
  firmwareUpdate: 'Firmware Update',
  pushNotifications: 'Push Notifications',
  cloudUploadManual: 'Cloud Upload Manual',
  
  // Streaming and Media
  vodDownload: 'VOD Download',
  myBlackVueFilePlay: 'My BlackVue File Play',
  cloudFilePlay: 'Cloud File Play',
  fileDelete: 'File Delete',
  liveView: 'Live View',
  fileMove: 'File Move',
  
  // Tracking and Monitoring
  gpsTracking: 'GPS Tracking',
  geoFence: 'Geo Fencing',
  driveReports: 'Drive Reports',
  
  // User Management
  membersInquiry: 'Members Inquiry',
  inviteSubMaster: 'Invite SubMaster',
  inviteUser: 'Invite User',
  deleteSubMaster: 'Delete SubMaster',
  deleteUser: 'Delete User',
  
  // Group Management
  registerGroup: 'Register Group',
  deleteGroup: 'Delete Group',
  changeGroupName: 'Change Group Name',
  assignDashcamToGroup: 'Assign Dashcam to Group',
  deleteDashcamFromGroup: 'Delete Dashcam from Group',
  assignSubMasterToGroup: 'Assign SubMaster to Group',
  deleteSubMasterFromGroup: 'Delete SubMaster from Group',
  assignUserToDashcam: 'Assign User to Dashcam',
  deleteUserFromDashcam: 'Delete User from Dashcam',
  
  // Advanced Permissions
  setSubMasterPermissions: 'Set SubMaster Permissions',
  inquirySubMasterPermissions: 'Inquiry SubMaster Permissions',
  setPushNotification: 'Set Push Notification',
  setEmailNotification: 'Set Email Notification',
  sharingVideoToEventMap: 'Sharing Video to Event Map',
  
  // Storage
  cloudStorageUsage: 'Cloud Storage Usage',
  
  // Compliance
  eldHosCompliance: 'ELD HOS Compliance'
};

/**
 * Permission categories for UI organization
 */
export const PERMISSION_CATEGORIES = {
  deviceManagement: [
    'listDashcam', 
    'registerDashcam', 
    'changeDashcamName', 
    'deleteDashcam', 
    'dashcamInformation'
  ],
  deviceSettings: [
    'firmwareSettings', 
    'privacy', 
    'eventAutoUpload', 
    'firmwareUpdate', 
    'pushNotifications', 
    'cloudUploadManual'
  ],
  streamingMedia: [
    'vodDownload', 
    'myBlackVueFilePlay', 
    'cloudFilePlay', 
    'fileDelete', 
    'liveView', 
    'fileMove'
  ],
  trackingMonitoring: [
    'gpsTracking', 
    'geoFence', 
    'driveReports'
  ],
  userManagement: [
    'membersInquiry', 
    'inviteSubMaster', 
    'inviteUser', 
    'deleteSubMaster', 
    'deleteUser'
  ],
  groupManagement: [
    'registerGroup', 
    'deleteGroup', 
    'changeGroupName', 
    'assignDashcamToGroup', 
    'deleteDashcamFromGroup', 
    'assignSubMasterToGroup', 
    'deleteSubMasterFromGroup', 
    'assignUserToDashcam', 
    'deleteUserFromDashcam'
  ],
  advanced: [
    'setSubMasterPermissions', 
    'inquirySubMasterPermissions', 
    'setPushNotification', 
    'setEmailNotification', 
    'sharingVideoToEventMap'
  ],
  storage: [
    'cloudStorageUsage'
  ],
  compliance: [
    'eldHosCompliance'
  ]
} as const;